import { apiClient } from '@/lib/apiClient';

// Enhanced error handling wrapper
const handleApiError = (error: any, operation: string) => {
  console.error(`RBAC API Error [${operation}]:`, error);
  
  if (error.response) {
    // Server responded with error status
    throw new Error(`${operation} failed: ${error.response.data?.detail || error.response.statusText}`);
  } else if (error.request) {
    // Request made but no response
    throw new Error(`${operation} failed: No response from server`);
  } else {
    // Something else happened
    throw new Error(`${operation} failed: ${error.message}`);
  }
};

// User CRUD
export async function fetchUsers() {
  try {
    console.log('📡 Fetching users');
    const data = await apiClient.get('/rbac/users');
    console.log('✅ Fetch users successful:', data?.length || 0, 'users');
    return data;
  } catch (error) {
    console.error('❌ Fetch users failed:', error);
    return [];
  }
}

export async function createUser(user: any) {
  try {
    console.log('📡 Creating user');
    const data = await apiClient.post('/rbac/users', user);
    console.log('✅ Create user successful:', data);
    return data;
  } catch (error) {
    console.error('❌ Create user failed:', error);
    handleApiError(error, "Create User");
  }
}

export async function updateUser(userId: number, user: any) {
  try {
    console.log('📡 Updating user:', userId);
    const { role_ids, ...userInfo } = user;
    
    const userData = await apiClient.put(`/rbac/users/${userId}`, userInfo);
    console.log('✅ User info updated successfully');
    
    if (role_ids !== undefined && Array.isArray(role_ids)) {
      console.log('📡 Updating user roles');
      const roleAssignment = { user_id: userId, role_ids };
      
      try {
        await apiClient.post('/rbac/user_roles/bulk', roleAssignment);
        console.log('✅ User roles updated successfully');
      } catch (roleError) {
        console.warn('⚠️ Role update failed, but user info was updated');
      }
    }
    
    return userData;
  } catch (error) {
    console.error('❌ Update user failed:', error);
    handleApiError(error, "Update User");
  }
}

export async function updateUserStatus(userId: number, isActive: boolean) {
  try {
    return await apiClient.put(`/rbac/users/${userId}/status`, { is_active: isActive });
  } catch (error) {
    handleApiError(error, "Update User Status");
  }
}

export async function deleteUser(userId: number) {
  try {
    return await apiClient.delete(`/rbac/users/${userId}`);
  } catch (error) {
    handleApiError(error, "Delete User");
  }
}

// Role CRUD
export async function fetchRoles() {
  try {
    console.log('📡 Fetching roles');
    const data = await apiClient.get('/rbac/roles');
    console.log('✅ Fetch roles successful:', data?.length || 0, 'roles');
    return data;
  } catch (error) {
    console.error('❌ Fetch roles failed:', error);
    return [];
  }
}

export async function fetchRolesHierarchy() {
  try {
    return await apiClient.get('/rbac/roles/hierarchy');
  } catch (error) {
    handleApiError(error, "Fetch Roles Hierarchy");
  }
}

export async function createRole(role: any) {
  try {
    return await apiClient.post('/rbac/roles', role);
  } catch (error) {
    handleApiError(error, "Create Role");
  }
}

export async function updateRole(roleId: string, role: any) {
  try {
    return await apiClient.put(`/rbac/roles/${roleId}`, role);
  } catch (error) {
    handleApiError(error, "Update Role");
  }
}

export async function deleteRole(roleId: string) {
  try {
    return await apiClient.delete(`/rbac/roles/${roleId}`);
  } catch (error) {
    handleApiError(error, "Delete Role");
  }
}

// Permission CRUD
export async function fetchPermissions() {
  try {
    console.log('📡 Fetching permissions');
    const data = await apiClient.get('/rbac/permissions');
    console.log('✅ Fetch permissions successful:', data?.length || 0, 'permissions');
    return data;
  } catch (error) {
    console.error('❌ Fetch permissions failed:', error);
    return [];
  }
}

export async function fetchPermissionsByResource(resourceId: string) {
  try {
    return await apiClient.get(`/rbac/permissions/by-resource/${resourceId}`);
  } catch (error) {
    handleApiError(error, "Fetch Permissions By Resource");
  }
}

export async function createPermission(permission: any) {
  try {
    return await apiClient.post('/rbac/permissions', permission);
  } catch (error) {
    handleApiError(error, "Create Permission");
  }
}

export async function updatePermission(permissionId: string, permission: any) {
  try {
    return await apiClient.put(`/rbac/permissions/${permissionId}`, permission);
  } catch (error) {
    handleApiError(error, "Update Permission");
  }
}

export async function deletePermission(permissionId: string) {
  try {
    return await apiClient.delete(`/rbac/permissions/${permissionId}`);
  } catch (error) {
    handleApiError(error, "Delete Permission");
  }
}

// Resource CRUD
export async function fetchResources() {
  try {
    console.log('📡 Fetching resources');
    const data = await apiClient.get('/rbac/resources');
    console.log('✅ Fetch resources successful:', data?.length || 0, 'resources');
    return data;
  } catch (error) {
    console.error('❌ Fetch resources failed:', error);
    handleApiError(error, "Fetch Resources");
  }
}

export async function fetchResourcesTree() {
  try {
    return await apiClient.get('/rbac/resources/tree');
  } catch (error) {
    handleApiError(error, "Fetch Resources Tree");
  }
}

export async function createResource(resource: any) {
  try {
    return await apiClient.post('/rbac/resources', resource);
  } catch (error) {
    handleApiError(error, "Create Resource");
  }
}

// Action CRUD
export async function fetchActions() {
  try {
    return await apiClient.get('/rbac/actions');
  } catch (error) {
    handleApiError(error, "Fetch Actions");
  }
}

export async function createAction(action: any) {
  try {
    return await apiClient.post('/rbac/actions', action);
  } catch (error) {
    handleApiError(error, "Create Action");
  }
}

export async function updateAction(actionId: string, action: any) {
  try {
    return await apiClient.put(`/rbac/actions/${actionId}`, action);
  } catch (error) {
    handleApiError(error, "Update Action");
  }
}

export async function deleteAction(actionId: string) {
  try {
    return await apiClient.delete(`/rbac/actions/${actionId}`);
  } catch (error) {
    handleApiError(error, "Delete Action");
  }
}

// Resource Management
export async function updateResource(resourceId: string, resource: any) {
  try {
    return await apiClient.put(`/rbac/resources/${resourceId}`, resource);
  } catch (error) {
    handleApiError(error, "Update Resource");
  }
}

export async function deleteResource(resourceId: string) {
  try {
    return await apiClient.delete(`/rbac/resources/${resourceId}`);
  } catch (error) {
    handleApiError(error, "Delete Resource");
  }
}

// Permission Discovery & Suggestions
export async function getPermissionSuggestions() {
  try {
    return await apiClient.get('/rbac/permissions/suggestions');
  } catch (error) {
    handleApiError(error, "Get Permission Suggestions");
  }
}

export async function getMissingPermissions(resourceId: string) {
  try {
    return await apiClient.get(`/rbac/permissions/missing/${resourceId}`);
  } catch (error) {
    handleApiError(error, "Get Missing Permissions");
  }
}

export async function getUnmappedResources() {
  try {
    return await apiClient.get('/rbac/resources/unmapped');
  } catch (error) {
    handleApiError(error, "Get Unmapped Resources");
  }
}

// Enhanced Smart Bulk Operations
export type ConflictMode = 'skip_existing' | 'update_existing' | 'force_replace';

export interface SmartBulkPermissionRequest {
  permissions: any[];
  mode: ConflictMode;
}

export interface PermissionCheckRequest {
  resource_id: string;
  permission_ids: string[];
}

export async function checkExistingPermissions(checkRequest: PermissionCheckRequest) {
  try {
    console.log('🔍 Checking existing permissions');
    const data = await apiClient.post('/rbac/permissions/check-existing', checkRequest);
    console.log('✅ Permission check completed');
    return data;
  } catch (error) {
    console.error('❌ Permission check failed:', error);
    handleApiError(error, "Check Existing Permissions");
  }
}

export async function createSmartBulkPermissions(bulkRequest: SmartBulkPermissionRequest) {
  try {
    console.log('🧠 Creating smart bulk permissions:', bulkRequest.permissions.length, 'with mode:', bulkRequest.mode);
    const data = await apiClient.post('/rbac/permissions/bulk-smart', bulkRequest);
    console.log('✅ Smart bulk permissions created');
    return data;
  } catch (error) {
    console.error('❌ Smart bulk permissions creation failed:', error);
    handleApiError(error, "Create Smart Bulk Permissions");
  }
}

export async function comparePermissions(resourceId: string) {
  try {
    console.log('📊 Comparing permissions for resource:', resourceId);
    const data = await apiClient.get(`/rbac/permissions/compare/${resourceId}`);
    console.log('✅ Permission comparison completed');
    return data;
  } catch (error) {
    console.error('❌ Permission comparison failed:', error);
    handleApiError(error, "Compare Permissions");
  }
}

export async function cleanupResourcePermissions(resourceId: string) {
  try {
    console.log('🗑️ Cleaning up permissions for resource:', resourceId);
    const data = await apiClient.delete(`/rbac/permissions/resource/${resourceId}`);
    console.log('✅ Permission cleanup completed');
    return data;
  } catch (error) {
    console.error('❌ Permission cleanup failed:', error);
    handleApiError(error, "Cleanup Resource Permissions");
  }
}

// Legacy bulk operations (deprecated - use smart versions)
export async function createBulkPermissions(permissions: any[]) {
  try {
    console.log('📡 Creating bulk permissions (legacy):', permissions.length);
    const data = await apiClient.post('/rbac/permissions/bulk', { permissions });
    console.log('✅ Bulk permissions created successfully');
    return data;
  } catch (error) {
    console.error('❌ Bulk permissions creation failed:', error);
    handleApiError(error, "Create Bulk Permissions");
  }
}

export async function createStandardPermissions(resourceId: string, mode: ConflictMode = 'skip_existing') {
  try {
    // Create standard permissions using the smart bulk permissions endpoint
    const standardPermissions = [
      { id: `${resourceId}.read`, name: `View ${resourceId}`, description: `View ${resourceId} information`, resource_id: resourceId, action_id: 'read' },
      { id: `${resourceId}.create`, name: `Create ${resourceId}`, description: `Add new ${resourceId}`, resource_id: resourceId, action_id: 'create' },
      { id: `${resourceId}.update`, name: `Update ${resourceId}`, description: `Modify ${resourceId}`, resource_id: resourceId, action_id: 'update' },
      { id: `${resourceId}.delete`, name: `Delete ${resourceId}`, description: `Remove ${resourceId}`, resource_id: resourceId, action_id: 'delete' },
      { id: `${resourceId}.export`, name: `Export ${resourceId}`, description: `Export ${resourceId} data`, resource_id: resourceId, action_id: 'export' },
      { id: `${resourceId}.manage`, name: `Manage ${resourceId}`, description: `Full ${resourceId} management access`, resource_id: resourceId, action_id: 'manage' }
    ];
    
    console.log('🚀 Creating standard permissions via smart bulk endpoint:', standardPermissions, 'mode:', mode);
    const result = await createSmartBulkPermissions({ permissions: standardPermissions, mode });
    console.log('✅ Standard permissions created successfully:', result);
    return result;
  } catch (error) {
    console.error('❌ Standard permissions creation failed:', error);
    handleApiError(error, "Create Standard Permissions");
  }
}

// Permission Matrix
export async function fetchPermissionMatrix() {
  try {
    console.log('📡 Fetching permission matrix');
    const data = await apiClient.get('/rbac/role_permissions/matrix');
    console.log('✅ Permission matrix fetched');
    return data;
  } catch (error) {
    console.error('❌ Permission matrix fetch failed:', error);
    handleApiError(error, "Fetch Permission Matrix");
  }
}

export async function updatePermissionMatrix(updates: any[]) {
  try {
    return await apiClient.post('/rbac/role_permissions/matrix/update', updates);
  } catch (error) {
    handleApiError(error, "Update Permission Matrix");
  }
}

// Bulk Operations
export async function bulkAssignUserRoles(assignment: any) {
  try {
    return await apiClient.post('/rbac/user_roles/bulk', assignment);
  } catch (error) {
    handleApiError(error, "Bulk Assign User Roles");
  }
}

// Permission Checking
export async function checkPermission(check: any) {
  try {
    return await apiClient.post('/rbac/check_permission', check);
  } catch (error) {
    handleApiError(error, "Check Permission");
  }
}

export async function getEffectivePermissions(userId: number) {
  try {
    console.log('📡 Fetching effective permissions for user:', userId);
    const data = await apiClient.get(`/rbac/users/${userId}/permissions/effective`);
    console.log('✅ Effective permissions fetched:', Array.isArray(data) ? data.length : data);
    return data;
  } catch (error) {
    handleApiError(error, "Get Effective Permissions");
  }
}

export async function getUserAccessSummary(userId: number) {
  try {
    return await apiClient.get(`/rbac/users/${userId}/access-summary`);
  } catch (error) {
    handleApiError(error, "Get User Access Summary");
  }
}

// Security Events
export async function fetchSecurityEvents(limit: number = 100) {
  try {
    return await apiClient.get(`/rbac/security_events?limit=${limit}`);
  } catch (error) {
    handleApiError(error, "Fetch Security Events");
  }
}
